//#region Imports
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { EMPLOYMENT_STATUS_VALIDATIONS, NOT_APPLICABLE, NO_SYMBOLS_OR_SPACES_PATTERN, PHONE_FORMAT, SURVEYSTEPS_INDEX } from '@app/member-response-portal/model/constants';
import { EmploymentStatusDto } from '@app/member-response-portal/model/employmentStatusDto';
import { EmploymentStatusResultDto } from '@app/member-response-portal/model/employmentStatusResultDto';
import { EmploymentStatusService } from '@app/member-response-portal/service/employment-status.service';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { GenericMemberResponsePortalResponseDto } from '@app/core/models/genericMembeResponsePortalResponseDto';
import { Helper } from '@rawlings/core';
import { MemberResponseHelper } from '@app/member-response-portal/shared/member-response-helper';
import { NotificationRequestService } from '@app/member-response-portal/service/notification-request.service';
import { NotifierService } from '@app/core/services/notifier-service';
import { Observable, catchError, map, of } from 'rxjs';
import { SharedMemberResponseService } from '@app/member-response-portal/service/shared-member-response.service';
import { SurveyStepBase } from '@app/member-response-portal/shared/survey-step-base';
//#endregion Imports
@Component({
    selector: 'app-survey-employment-status',
    templateUrl: './survey-employment-status.component.html',
    styleUrls: ['./survey-employment-status.component.scss'],
})
export class SurveyEmploymentStatusComponent extends SurveyStepBase implements OnInit
{
    //#region Public Variables
    public PhoneFormat: string = PHONE_FORMAT;
    public ShowCurrentlyWorking: boolean = false;
    public CurrentlyWorking: boolean = false;
    public ShowSpouseCurrentlyWorkingQuestion: boolean = false;
    public ShowSpouseCurrentlyWorking: boolean = false;
    public SpouseCurrentlyWorking: boolean = null;
    public SpouseToggleSkipModalFirstSelection: boolean = true;
    public NameMaxLength: number = EMPLOYMENT_STATUS_VALIDATIONS.NAME_MAX_LENGTH;
    public EmployerNameMaxLength: number =
    EMPLOYMENT_STATUS_VALIDATIONS.EMPLOYER_NAME_MAX_LENGTH;
    //#endregion Public Variables

    //#region Private Variables
    //#endregion Private Variables

    //#region Properties

    public get DateLastWorked(): FormControl {
        return this.Form.get('DateLastWorked') as FormControl;
    }

    public get EmployerName(): FormControl {
        return this.Form.get('EmployerName') as FormControl;
    }

    public get EmployerPhone(): FormControl {
        return this.Form.get('EmployerPhone') as FormControl;
    }

    public get HasSpouse(): FormControl {
        return this.Form.get('HasSpouse') as FormControl;
    }
    public get IsCurrentlyWorking(): FormControl {
        return this.Form.get('IsCurrentlyWorking') as FormControl;
    }

    public get Name(): FormControl {
        return this.Form.get('Name') as FormControl;
    }

    public get SpouseDateLastWorked(): FormControl {
        return this.Form.get('SpouseDateLastWorked') as FormControl;
    }

    public get SpouseIsCurrentlyWorking(): FormControl {
        return this.Form.get('SpouseIsCurrentlyWorking') as FormControl;
    }

    public get SpouseEmployerName(): FormControl {
        return this.Form.get('SpouseEmployerName') as FormControl;
    }

    public get SpouseEmployerPhone(): FormControl {
        return this.Form.get('SpouseEmployerPhone') as FormControl;
    }

    public get SpouseName(): FormControl {
        return this.Form.get('SpouseName') as FormControl;
    }

    public get EmploymentStatusDto(): EmploymentStatusDto {
        const hasSpouse: boolean = this.HasSpouse?.value ?? false;

        const employmentStatusDto: EmploymentStatusDto = {
            QueueLetterId: this.sharedMemberResponseService.GetQueueLetterId(),
            SurveyResponseId: this.SurveyResponseId,
            IsCurrentlyWorking: this.CurrentlyWorking,
            Name: MemberResponseHelper.GetValidString(this.Name.value),
            CurrentEmployerName: this.CurrentlyWorking
            ? MemberResponseHelper.GetValidString(this.EmployerName.value)
            : null,
            CurrentEmployerPhone: this.CurrentlyWorking
            ? MemberResponseHelper.GetValidPhoneNumber(this.EmployerPhone.value)
            : null,
            FormerEmployerName: !this.CurrentlyWorking
            ? MemberResponseHelper.GetValidString(this.EmployerName.value)
            : null,
            FormerEmployerPhone: !this.CurrentlyWorking
            ? MemberResponseHelper.GetValidPhoneNumber(this.EmployerPhone.value)
            : null,
            DateLastWorked: !this.CurrentlyWorking
            ? MemberResponseHelper.getDateWithZeroTime(this.DateLastWorked.value)
            : null,
            HasSpouse: hasSpouse,
            SpouseIsCurrentlyWorking: hasSpouse ? this.SpouseCurrentlyWorking : null,
            SpouseName: hasSpouse
            ? MemberResponseHelper.GetValidString(this.SpouseName.value)
            : null,
            SpouseCurrentEmployerName:
            hasSpouse && this.SpouseCurrentlyWorking
            ? MemberResponseHelper.GetValidString(this.SpouseEmployerName.value)
            : null,
            SpouseCurrentEmployerPhone:
            hasSpouse && this.SpouseCurrentlyWorking
            ? MemberResponseHelper.GetValidPhoneNumber(
                this.SpouseEmployerPhone.value
            )
            : null,
            SpouseFormerEmployerName:
            hasSpouse && !this.SpouseCurrentlyWorking
            ? MemberResponseHelper.GetValidString(this.SpouseEmployerName.value)
            : null,
            SpouseFormerEmployerPhone:
            hasSpouse && !this.SpouseCurrentlyWorking
            ? MemberResponseHelper.GetValidPhoneNumber(
                this.SpouseEmployerPhone.value
            )
            : null,
            SpouseDateLastWorked:
            hasSpouse && !this.SpouseCurrentlyWorking
            ? MemberResponseHelper.getDateWithZeroTime(
                this.SpouseDateLastWorked.value
            )
            : null,
            IsCompleted: this.Form.valid,
        };
        return employmentStatusDto;
    }

    //#endregion Properties

    //#region Constructor
    constructor(
        protected notifierService: NotifierService,
        protected sharedMemberResponseService: SharedMemberResponseService,
        protected changeDetectorRef: ChangeDetectorRef,
        private fb: FormBuilder,
        private notificationRequestService: NotificationRequestService,
        private employmentStatusService: EmploymentStatusService
    ) {
        super(
            `Employment Status`,
            SURVEYSTEPS_INDEX.EMPLOYMENT_STATUS,
            false,
            'EmploymentStatus',
            changeDetectorRef,
            notifierService,
            sharedMemberResponseService
        );
    }

    public ngOnInit(): void {
        this.InitStep();

        const controlsToValidate : string[] = ['Name', 'EmployerName', 'SpouseName', 'SpouseEmployerName'];
        const validators : ValidatorFn = Validators.pattern(NO_SYMBOLS_OR_SPACES_PATTERN);

        controlsToValidate.forEach((control : string ) => {
            this.Form.get(control).addValidators(validators);
        });
    }

    //#endregion Constructor

    //#region Public Methods

    public CreateEmploymentStatusFormGroup(): FormGroup {
        return this.fb.group({
            SurveyResponseId: [this.SurveyResponseId, Validators.required],
            IsCurrentlyWorking: ['', Validators.required],
            Name: [null, Validators.required],
            EmployerName: [null, Validators.required],
            EmployerPhone: [null, Validators.required],
            DateLastWorked: [null],
            HasSpouse: [null, Validators.required],
            SpouseIsCurrentlyWorking: [null],
            SpouseName: [null],
            SpouseEmployerName: [null],
            SpouseEmployerPhone: [null],
            SpouseDateLastWorked: [null],
        });
    }

    public HandleMemberToggleEvent(event: {
        IsChecked: boolean;
        IsConfirmed: boolean;
    }): void {
        this.ShowSpouseCurrentlyWorkingQuestion = true;
        this.ShowCurrentlyWorking = true;
        this.CurrentlyWorking = event.IsChecked;
        this.handleCurrentlyWorkingCalendarValidation();
    }

    public HandleSpouseToggleEvent(event: {
        IsChecked: boolean;
        IsConfirmed: boolean;
    }): void {
        if (Helper.IsBoolean(event.IsChecked)) {
            this.HasSpouse?.setValue(true);
            this.ShowSpouseCurrentlyWorking = true;
            this.SpouseCurrentlyWorking = event.IsChecked;
        } else if (event.IsConfirmed) {
            this.ShowSpouseCurrentlyWorking = false;
            this.HasSpouse?.setValue(false);
            this.resetSpouseFormValues();
        } else {
            this.Form.patchValue({
                SpouseIsCurrentlyWorking: MemberResponseHelper.GetStringForYesNoValue(
                    this.SpouseCurrentlyWorking
                ),
            });
        }
        this.handleConditionalMandatoryFields();
    }

    public SkipSpouseToggleU(): boolean {
        return !this.ShowSpouseCurrentlyWorking ? true : false;
    }

    public SetErrorTooltipMessage(value: string, message: string) : string {
        if (this.Form.get(value).hasError('pattern')) {
            return `Invalid ${message}`;
        }

        return null;
    }
    //#endregion Public Methods

    //#region Protected Methods
    protected BuildForm(): void {
        this.Form = this.CreateEmploymentStatusFormGroup();
    }

    protected GetFormData() : any {
        return this.EmploymentStatusDto;
    }

    protected HandleRequest(): Observable<boolean> {
        return this.employmentStatusService.Save(this.EmploymentStatusDto).pipe(
            map(
                (
                    result: GenericMemberResponsePortalResponseDto<EmploymentStatusResultDto>
                ): boolean => {
                    if (!result.Success) {
                        result.IsSurveySubmitted ? this.notificationRequestService.ShowFailedRequestAndLogout(result) : this.notificationRequestService.ShowFailedRequestAndLogout(null);
                        return false;
                    }

                    return true;
                }
            ),
            catchError(() => {
                return of(false);
            })
        );
    }

    protected LoadData(): void {
        const ininitialData: any = this.sharedMemberResponseService.GetSurveySummarySection(this.FormKey);

        if(ininitialData){
            this.SpouseToggleSkipModalFirstSelection = false;
            this.setFormValues(ininitialData);
            this.handleCurrentlyWorkingCalendarValidation();
            this.handleConditionalMandatoryFields();
            this.Form.updateValueAndValidity();
        }
    }
    //#endregion Protected Methods

    //#region Private Methods
    private handleConditionalMandatoryFields(): void {
        const spouseControls: FormControl[] = [
            this.SpouseName,
            this.SpouseEmployerName,
            this.SpouseEmployerPhone,
        ];

        const hasSpouse: boolean = this.HasSpouse?.value ?? false;

        if (hasSpouse) {
            spouseControls.forEach((control: FormControl) => {
                control?.addValidators(Validators.required);
            });

            if (!this.SpouseCurrentlyWorking) {
                this.SpouseDateLastWorked?.addValidators(Validators.required);
            } else {
                this.SpouseDateLastWorked?.removeValidators(Validators.required);
            }
        } else {
            spouseControls.forEach((control: FormControl) => {
                control?.removeValidators(Validators.required);
            });
            this.SpouseDateLastWorked?.removeValidators(Validators.required);
        }

        spouseControls.forEach((control: FormControl) => {
            control?.updateValueAndValidity();
        });

        this.SpouseDateLastWorked?.updateValueAndValidity();
    }

    private handleCurrentlyWorkingCalendarValidation(): void {
        if (!this.CurrentlyWorking) {
            this.DateLastWorked?.addValidators(Validators.required);
        } else {
            this.DateLastWorked?.removeValidators(Validators.required);
        }

        this.DateLastWorked?.updateValueAndValidity();
    }

    private resetSpouseFormValues(): void {
        this.SpouseDateLastWorked.reset();
        this.SpouseEmployerName.reset();
        this.SpouseEmployerPhone.reset();
        this.SpouseName.reset();
    }

    private setFormValues(employmentStatusDto: EmploymentStatusDto): void {
        this.Form.patchValue({
            SurveyResponseId: this.SurveyResponseId,
            IsCurrentlyWorking: MemberResponseHelper.GetStringForYesNoValue(
                employmentStatusDto.IsCurrentlyWorking
            ),
            Name: employmentStatusDto.Name,
            EmployerName:
            employmentStatusDto.CurrentEmployerName ??
            employmentStatusDto.FormerEmployerName,
            EmployerPhone:
            employmentStatusDto.CurrentEmployerPhone ??
            employmentStatusDto.FormerEmployerPhone,
            DateLastWorked: employmentStatusDto.DateLastWorked,
            SpouseIsCurrentlyWorking: employmentStatusDto.HasSpouse
            ? MemberResponseHelper.GetStringForYesNoValue(
                employmentStatusDto.SpouseIsCurrentlyWorking
            )
            : NOT_APPLICABLE,
            SpouseName: employmentStatusDto.SpouseName,
            SpouseEmployerName:
            employmentStatusDto.SpouseCurrentEmployerName ??
            employmentStatusDto.SpouseFormerEmployerName,
            SpouseEmployerPhone:
            employmentStatusDto.SpouseCurrentEmployerPhone ??
            employmentStatusDto.SpouseFormerEmployerPhone,
            SpouseDateLastWorked: employmentStatusDto.SpouseDateLastWorked,
            IsCompleted: this.Form.valid,
            HasSpouse: employmentStatusDto.HasSpouse,
        });

        this.CurrentlyWorking = employmentStatusDto.IsCurrentlyWorking;
        this.ShowCurrentlyWorking = true;
        this.ShowSpouseCurrentlyWorkingQuestion = true;
        this.ShowSpouseCurrentlyWorking = employmentStatusDto.HasSpouse;
        this.SpouseCurrentlyWorking = employmentStatusDto.SpouseIsCurrentlyWorking;
    }
    //#endregion Private Methods
}
